// external
import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useParams } from 'react-router-dom';

// internal
import './team-details.page.css';
import useService from '../../hooks/service.hook';
import TeamsService from '../../services/teams.service';
import GuardianService from '../../services/guardian.service';
import { Team } from '../../models/training.models';
import TeamRosterTab from './pages/team-management/tabs/team-roster.tab';
import MessageBoardPage from './pages/message-board/message-board.page';
import TeamTrainingsPage from './pages/team-trainings/team-trainings.page';
import TeamDashboardPage from './pages/team-dashboard/team-dashboard.page';
import AthleteSchedulesPage from './pages/athlete-schedules/athlete-schedules.page';
import EquipmentPage from '../equipment/equipment.page';
import RaceLogPage from './pages/race-log/race-log.page';
import { TEAM_DETAILS_TABS } from './team-details.constants';
import COETeamsHeaderV2Component from '../../components/coe-header/coe-teams-header-v2.component';
import ManageTeamDialog from '../../components/dialogs/manage-team.dialog';
import {
  TEAM_DETAILS_STORAGE_KEYS,
  clearStoredValue,
  getStoredValue,
  setStoredValue,
} from '../../helpers/cache.helpers';

const emptyTeamDetails: any = {
  id: null,
  name: '',
  color: '',
  coach: '',
  description: '',
  role: 'athlete',
  status: '',
  groups: [],
};

const mapWardTeamToTeam = (data: any, routeId: string): Team => {
  const id = String(data?.id ?? data?.teamId ?? routeId);
  return {
    ...emptyTeamDetails,
    ...data,
    id,
    teamId: id,
    name: data?.name || data?.team_name || '',
    color: data?.color || data?.team_color || '',
    role: 'guardian',
    status: data?.status || 'active',
    groups: data?.groups || [],
    description: data?.description || '',
    teamImage: data?.teamImage ?? null,
    stats: data?.stats,
    memberCounts: data?.memberCounts,
    quickInfo: data?.quickInfo,
    plan: data?.plan,
  };
};

export interface TeamDetailsPageProps {
  view?: string;
  tab: string;
}

const COACH_ONLY_VIEWS = ['team-trainings', 'athlete-schedules', 'equipment'];

const TeamDetailsPage: FC<TeamDetailsPageProps> = (props) => {
  const teamsService = useService(TeamsService);
  const guardianService = useService(GuardianService);
  const navigate = useNavigate();
  const { id } = useParams();

  const [teamDetails, setTeamDetails] = useState<Team>(emptyTeamDetails);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState<boolean>(true);
  const [manageTeamOpen, setManageTeamOpen] = useState(false);
  const hasLoadedTeam = useRef(false);

  const isGuardian = teamDetails.role?.toLowerCase() === 'guardian';
  const isCoach = teamDetails.role?.toLowerCase() === 'coach';
  const paymentRequired = Boolean(teamDetails.billingRequired);

  useEffect(() => {
    hasLoadedTeam.current = false;
    getTeamDetails();
  }, [id]);

  useEffect(() => {
    if (teamDetailsLoading) return;
    if (!props.view || !COACH_ONLY_VIEWS.includes(props.view)) return;
    if (isCoach) return;
    if (!teamDetails.id) return;
    navigate(`/teams/${teamDetails.id}/dashboard`, { replace: true });
  }, [props.view, teamDetails.id, isCoach, teamDetailsLoading, navigate]);

  const goBackToTeams = () => {
    clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
    navigate('/teams');
  };

  const getTeamDetails = () => {
    if (!id) return;
    // A refresh while the roster is open must not unmount it. That was closing the
    // first-visit invite popup as soon as the team code was generated.
    if (!hasLoadedTeam.current) setTeamDetailsLoading(true);
    teamsService
      .getTeamDetails(id)
      .then((response: any) => {
        const nextTeam = response.data.data;
        const status = String(nextTeam?.status || '').toLowerCase();
        if (status === 'cancelled' || status === 'canceled') {
          clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
          navigate('/teams', { replace: true });
          return;
        }
        setTeamDetails(nextTeam);
        setStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, String(id));
        hasLoadedTeam.current = true;
        setTeamDetailsLoading(false);
      })
      .catch(() => {
        guardianService
          .getWardTeamDetails(id)
          .then((response: any) => {
            setTeamDetails(mapWardTeamToTeam(response.data.data, id));
            setStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, String(id));
            hasLoadedTeam.current = true;
            setTeamDetailsLoading(false);
          })
          .catch(() => {
            if (getStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM, '') === String(id)) {
              clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
            }
            navigate('/teams');
          });
      });
  };

  const renderPaymentLock = () => (
    <Box className="team-payment-lock">
      <Box className="team-payment-lock-card">
        <LockOutlinedIcon className="team-payment-lock-icon" />
        <Typography className="team-payment-lock-title">This team is locked</Typography>
        <Typography className="team-payment-lock-body">
          {isCoach
            ? 'Your free trial has ended. Pay on Billing to unlock roster, trainings, messages, and team settings.'
            : 'This team is locked until the coach completes payment. You can go back to My Teams anytime.'}
        </Typography>
        <Box className="team-payment-lock-actions">
          <Button variant="outlined" onClick={goBackToTeams}>
            Back to My Teams
          </Button>
          {isCoach ? (
            <Button variant="contained" onClick={() => navigate('/billing')}>
              Go to Billing
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  const renderInactiveTeamOverlay = () => (
    <div className="team-inactive-banner-wrap">
      <Box className="team-inactive-banner">
        Your team is no longer active. You can view your data but can no longer perform team actions.
      </Box>
    </div>
  );

  const renderTeamRoute = () => {
    const numericTeamId = Number(teamDetails.id ?? teamDetails.teamId);

    if (teamDetailsLoading) {
      return (
        <div className="team-details-loading">
          <CircularProgress style={{ width: 100, height: 100 }} />
        </div>
      );
    }

    if (paymentRequired) {
      return renderPaymentLock();
    }

    if (props.view === 'dashboard') {
      return <TeamDashboardPage team={teamDetails} />;
    }
    if (props.view === 'messages') {
      return <MessageBoardPage isCoach={isCoach} isGuardian={isGuardian} team={teamDetails} />;
    }
    if (props.view === 'roster') {
      return <TeamRosterTab team={teamDetails} refreshTeam={() => getTeamDetails()} />;
    }
    if (props.view === 'team-trainings') {
      if (!isCoach) return null;
      return <TeamTrainingsPage team={teamDetails} refreshTeam={() => getTeamDetails()} />;
    }
    if (props.view === 'athlete-schedules') {
      if (!isCoach) return null;
      return <AthleteSchedulesPage team={teamDetails} />;
    }
    if (props.view === 'equipment') {
      if (!isCoach || !Number.isFinite(numericTeamId)) {
        return null;
      }
      return <EquipmentPage teamId={numericTeamId} embedded />;
    }
    if (props.view === 'race-log') {
      return <RaceLogPage team={teamDetails} refreshTeam={() => getTeamDetails()} />;
    }
    return null;
  };

  const header = (
    <COETeamsHeaderV2Component
      teamName={teamDetails.name}
      teamImage={teamDetails.teamImage}
      role={teamDetails.role}
      memberCounts={paymentRequired ? undefined : teamDetails.memberCounts}
      teamId={teamDetails.id as string}
      loading={teamDetailsLoading}
      backLocation="/teams"
      onBack={goBackToTeams}
      showHero
      hideManageTeam={isGuardian || paymentRequired}
      tabs={paymentRequired ? [] : TEAM_DETAILS_TABS(id as string, isCoach)}
      onManageTeam={isGuardian || paymentRequired ? undefined : () => setManageTeamOpen(true)}
    />
  );

  return (
    <>
      <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {header}
          {renderTeamRoute()}
        </div>
      </div>
      {!paymentRequired && teamDetails.status !== 'active' && !teamDetailsLoading ? renderInactiveTeamOverlay() : null}
      {!isGuardian && !paymentRequired ? (
        <ManageTeamDialog
          open={manageTeamOpen}
          onClose={() => setManageTeamOpen(false)}
          team={teamDetails}
          refreshTeam={getTeamDetails}
        />
      ) : null}
    </>
  );
};

export default TeamDetailsPage;
